import { Suspense, lazy, type ReactNode } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { TenantConfigProvider, useTenantConfig } from "./contexts/TenantConfigContext";
import { isBeautySnsLabConfig, isBeautySnsLabHost } from "@/config/beautySnsLab";
import ErrorBoundary from "./components/ErrorBoundary";
import { CookieConsent } from "./components/CookieConsent";
import { Loader2 } from "lucide-react";

// Static imports - 初期表示に必要なコンポーネント
import Auth from "./pages/Auth";
import AppLayout from "./components/layout/AppLayout";
import StudentLayout from "./components/layout/StudentLayout";

// Lazy imports - コード分割対象のページコンポーネント
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Students = lazy(() => import("./pages/Students"));
const Progress = lazy(() => import("./pages/Progress"));
const Analytics = lazy(() => import("./pages/Analytics"));
const StudentAnalytics = lazy(() => import("./pages/StudentAnalytics"));
const ChatFeedback = lazy(() => import("./pages/ChatFeedback"));
const Chat = lazy(() => import("./pages/Chat"));
const Videos = lazy(() => import("./pages/Videos"));
const RiskChecker = lazy(() => import("./pages/RiskChecker"));
const RiskCheckerHistory = lazy(() => import("./pages/RiskCheckerHistory"));
const CoachingHome = lazy(() => import("./pages/CoachingHome"));
const CoachingTypeIndex = lazy(() => import("./pages/CoachingTypeIndex"));
const Coaching = lazy(() => import("./pages/Coaching"));
const Courses = lazy(() => import("./pages/Courses"));
const Escalations = lazy(() => import("./pages/Escalations"));
const FAQAggregation = lazy(() => import("./pages/FAQAggregation"));
const FAQClusterDetail = lazy(() => import("./pages/FAQClusterDetail"));
const ChatFeedbackList = lazy(() => import("./pages/ChatFeedbackList"));
const StudentDetail = lazy(() => import("./pages/StudentDetail"));
const AdminCourseDetail = lazy(() => import("./pages/AdminCourseDetail"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Unauthorized = lazy(() => import("./pages/Unauthorized"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Docs = lazy(() => import("./pages/Docs"));
const Settings = lazy(() => import("./pages/Settings"));
const LineSettings = lazy(() => import("./pages/admin/LineSettings"));
const LineMessages = lazy(() => import("./pages/admin/LineMessages"));
const OemQuizSettings = lazy(() => import("./pages/admin/oem/QuizSettings"));
const Profile = lazy(() => import("./pages/Profile"));
const LineLinkPage = lazy(() => import("./pages/LineLinkPage"));
const BeautySnsLabLanding = lazy(() => import("./pages/BeautySnsLabLanding"));

// Student pages
const StudentDashboard = lazy(() => import("./pages/student/Dashboard"));
const StudentCourses = lazy(() => import("./pages/student/Courses"));
const StudentProgress = lazy(() => import("./pages/student/Progress"));
const StudentChat = lazy(() => import("./pages/student/Chat"));
const StudentAchievements = lazy(() => import("./pages/student/Achievements"));
const StudentNotifications = lazy(() => import("./pages/student/Notifications"));
const StudentHelp = lazy(() => import("./pages/student/Help"));
const CourseDetail = lazy(() => import("./pages/student/CourseDetail"));
const LessonViewer = lazy(() => import("./pages/student/LessonViewer"));
const LessonQuiz = lazy(() => import("./pages/student/LessonQuiz"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const queryClient = new QueryClient();
const basename = (import.meta.env.BASE_URL ?? "/").replace(/\/+$/, "") || "/";

function RoleBasedRedirect() {
  const { user, loading } = useAuth();
  const { config, loading: configLoading } = useTenantConfig();
  const isBeautyTenant =
    isBeautySnsLabConfig(config) ||
    (typeof window !== "undefined" && isBeautySnsLabHost(window.location.hostname));

  if (loading || configLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (!user.role) {
    return <Navigate to="/auth?missingRole=1" replace />;
  }

  if (user.role === 'admin' || user.role === 'educator') {
    return <Navigate to="/dashboard" replace />;
  }

  return <Navigate to="/student/dashboard" replace />;
}

function TenantDocsRoute() {
  const { config, loading } = useTenantConfig();
  const isBeautyTenant =
    isBeautySnsLabConfig(config) ||
    (typeof window !== "undefined" && isBeautySnsLabHost(window.location.hostname));

  if (loading) return <PageLoader />;
  if (isBeautyTenant) return <Navigate to="/" replace />;

  return <Docs />;
}

function TenantFeatureRoute({
  feature,
  children,
}: {
  feature: "line_enabled" | "human_escalation_enabled" | "risk_checker";
  children: ReactNode;
}) {
  const { config, loading } = useTenantConfig();
  const isBeautyTenant =
    isBeautySnsLabConfig(config) ||
    (typeof window !== "undefined" && isBeautySnsLabHost(window.location.hostname));
  // risk_checker は Beautyテナントでも有効（他のfeatureはBeautyで無効）
  // TODO: risk_checker は useSchoolFeatures() で管理されているため、
  //       将来的には TenantFeatureRoute を useSchoolFeatures() 参照に統一すべき
  const enabled = feature === "risk_checker"
    ? (config?.feature_flags?.[feature] ?? true)
    : (isBeautyTenant ? false : config?.feature_flags?.[feature] ?? true);

  if (loading) return <PageLoader />;
  if (!enabled) return <Navigate to="/" replace />;

  return <>{children}</>;
}

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem storageKey="jarvis-theme">
        <TooltipProvider>
          <AuthProvider>
            <TenantConfigProvider>
              <Toaster />
              <Sonner />
              <CookieConsent />
              <BrowserRouter basename={basename}>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<RoleBasedRedirect />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/unauthorized" element={<Unauthorized />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/docs" element={<TenantDocsRoute />} />
                  <Route path="/beauty-sns-lab" element={<BeautySnsLabLanding />} />

                  {/* LINE Account Link - All authenticated users */}
                  <Route path="/settings/line-link" element={
                    <ProtectedRoute allowedRoles={['admin', 'educator', 'student']}>
                      <TenantFeatureRoute feature="line_enabled">
                        <LineLinkPage />
                      </TenantFeatureRoute>
                    </ProtectedRoute>
                  } />

                  {/* Staff Routes */}
                  <Route element={
                    <ProtectedRoute allowedRoles={['admin', 'educator']}>
                      <AppLayout />
                    </ProtectedRoute>
                  }>
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/students" element={<Students />} />
                    <Route path="/students/:id" element={<StudentDetail />} />
                    <Route path="/progress" element={<Progress />} />
                    <Route path="/analytics" element={<Analytics />} />
                    <Route path="/analytics/:userId" element={<StudentAnalytics />} />
                    <Route path="/feedback" element={<ChatFeedback />} />
                    <Route path="/chat" element={<Chat />} />
                    <Route path="/videos" element={<Videos />} />
                    <Route path="/risk-checker" element={
                      <TenantFeatureRoute feature="risk_checker">
                        <RiskChecker />
                      </TenantFeatureRoute>
                    } />
                    <Route path="/risk-checker/history" element={
                      <TenantFeatureRoute feature="risk_checker">
                        <RiskCheckerHistory />
                      </TenantFeatureRoute>
                    } />
                    <Route path="/courses" element={<Courses />} />
                    <Route path="/courses/:id" element={<AdminCourseDetail />} />
                    <Route path="/escalations" element={
                      <TenantFeatureRoute feature="human_escalation_enabled">
                        <Escalations />
                      </TenantFeatureRoute>
                    } />
                    <Route path="/admin/faq" element={<FAQAggregation />} />
                    <Route path="/admin/faq/:clusterId" element={<FAQClusterDetail />} />
                    <Route path="/admin/feedback" element={<ChatFeedbackList />} />
                    <Route
                      path="/admin/line-settings"
                      element={
                        <ProtectedRoute allowedRoles={['admin']}>
                          <LineSettings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/oem/quiz-settings"
                      element={
                        <ProtectedRoute allowedRoles={['admin']}>
                          <OemQuizSettings />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/admin/line-messages" element={<LineMessages />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/profile" element={<Profile />} />
                  </Route>

                  {/* Student Routes - staff can also access */}
                  <Route path="/student" element={
                    <ProtectedRoute allowedRoles={['student', 'admin', 'educator']}>
                      <StudentLayout />
                    </ProtectedRoute>
                  }>
                    <Route path="dashboard" element={<StudentDashboard />} />
                    <Route path="courses" element={<StudentCourses />} />
                    <Route path="courses/:courseId" element={<CourseDetail />} />
                    <Route path="progress" element={<StudentProgress />} />
                    <Route path="chat" element={<StudentChat />} />
                    <Route path="achievements" element={<StudentAchievements />} />
                    <Route path="notifications" element={<StudentNotifications />} />
                    <Route path="risk-checker" element={
                      <TenantFeatureRoute feature="risk_checker">
                        <RiskChecker />
                      </TenantFeatureRoute>
                    } />
                    <Route path="risk-checker/history" element={
                      <TenantFeatureRoute feature="risk_checker">
                        <RiskCheckerHistory />
                      </TenantFeatureRoute>
                    } />
                    <Route path="lesson/:lessonId" element={<LessonViewer />} />
                    <Route path="lesson/:lessonId/quiz" element={<LessonQuiz />} />
                    <Route path="coaching" element={<CoachingHome />} />
                    <Route path="coaching/:type" element={<CoachingTypeIndex />} />
                    <Route path="coaching/:type/new" element={<Coaching />} />
                    <Route path="coaching/:type/:sessionId" element={<Coaching />} />
                    <Route path="settings" element={<Settings />} />
                    <Route path="help" element={<StudentHelp />} />
                    <Route path="terms" element={<Terms />} />
                    <Route path="privacy" element={<Privacy />} />
                  </Route>

                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
            </TenantConfigProvider>
          </AuthProvider>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
